// Profile.tsx — postcard-style profile for both own and visitor view.
//
// Merged from the /profile-preview design (now retired). Two code paths
// share the same chassis: hero → action row (visitor only) → postcard
// → stats → closet. Own-view affordances (4-button toolbar, SWAP) are
// gated by isYou; visitor-only affordances (3-dot menu, Follow / Message,
// SHOP AT row) are gated by !isYou.
//
// All inline editors (bio, interests, marketplace shops, avatar) have
// been moved to /settings — the postcard surfaces those fields read-only
// and the user manages them via the gear icon in the toolbar.

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { Icon, Swatch, Chip } from "@/components/primitives";
import type { SwatchKind } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { BottomDock } from "@/components/BottomDock";
import { NotificationsBell } from "@/components/NotificationsBell";
import { ExportSalesButton } from "@/components/ExportSalesButton";
import { BadgeChip } from "@/components/BadgeChip";
import { BadgeDetailModal } from "@/components/BadgeDetailModal";
import { ReportDialog } from "@/components/ReportDialog";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useMyProfile, useScans } from "@/hooks/useFigData";
import { useUserBadges, type UserBadge } from "@/hooks/useUserBadges";
import { useCollectorTier } from "@/hooks/useCollectorTier";
import { useBulkPending } from "@/hooks/useBulkPending";
import { useTierFeatures } from "@/hooks/useTierFeatures";
import { useUnreadInbox, ensureConversation } from "@/hooks/useMessages";
import { useFollow } from "@/context/FollowContext";
import { useBlockUser } from "@/hooks/useBlockedUsers";
import { scanStore, type AIScan } from "@/lib/scanStore";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { parseMarketplaceLinks, type MarketplaceLink } from "@/lib/marketplaces";
import { INTERESTS } from "@/lib/interests";

interface ProfileProps {
  forceYou?: boolean;
}

// ── Helpers (mirrored from ProfilePreview) ───────────────────────────────────

function memberNumberFor(userId: string | null | undefined): string {
  if (!userId) return "—";
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  const n = Math.abs(hash) % 999;
  return String(n).padStart(3, "0");
}

function formatSince(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" }).toUpperCase();
  } catch {
    return "—";
  }
}

function mastheadFontSize(len: number): number {
  if (len <= 10) return 48;
  if (len <= 14) return 42;
  if (len <= 18) return 36;
  if (len <= 22) return 30;
  return 24;
}

// ── Theme tokens ─────────────────────────────────────────────────────────────
const HERO_BG = "hsl(var(--ink))";
const HERO_FG = "hsl(var(--ink-fg))";
const BODY_BG = "hsl(var(--paper))";
const POSTCARD_BG_C = "hsl(var(--paper-2))";
const BORDER_C = "hsl(var(--sand-deep))";
const LABEL_INK = "hsl(var(--rust))";

const TOTAL_BADGE_KINDS = 7;

export default function Profile({ forceYou }: ProfileProps) {
  const nav = useNavigate();
  const params = useParams();
  const { user } = useAuth();
  const { isAdmin } = useAdminRole();
  const { profile } = useMyProfile();
  const { scans } = useScans();
  const { count: unreadInbox } = useUnreadInbox();
  const { features: bulkFeatures, loading: bulkLoading } = useTierFeatures();
  const { active: bulkActive, pending: bulkPending } = useBulkPending();

  const [statusTab, setStatusTab] = useState<"all" | "kept" | "listed" | "sold" | "loves">("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest">("newest");
  const [opening, setOpening] = useState<string | null>(null);

  // Visitor state
  const [otherProfile, setOtherProfile] = useState<{
    id: string;
    handle: string;
    display_name: string | null;
    avatar: string;
    bio: string | null;
    location: string | null;
    tags: string[];
    looking_for: string[];
    created_at: string;
  } | null>(null);
  const [otherScans, setOtherScans] = useState<Array<{
    id: string; title: string; img: string; era: string | null; category: string | null;
    price_suggested: number | null; verdict: string; posted: boolean; status: string | null;
    created_at: string; photos: string[] | null;
    posted_to: string | null; sold_at: string | null; sold_price: number | null;
    marketplace_links: unknown;
    result?: unknown; post_kind?: string | null;
  }>>([]);
  const [otherMarketplaceProfiles, setOtherMarketplaceProfiles] = useState<MarketplaceLink[]>([]);
  const [openingThread, setOpeningThread] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [postNotifyOn, setPostNotifyOn] = useState(false);
  const [savingPostNotify, setSavingPostNotify] = useState(false);
  const blockUser = useBlockUser();

  // Follower counts
  const [followerCount, setFollowerCount] = useState<number | null>(null);
  const [followingCount, setFollowingCount] = useState<number | null>(null);

  // Marketplace shops (own profile)
  const [myMarketplaceProfiles, setMyMarketplaceProfiles] = useState<MarketplaceLink[]>([]);

  // Badge state
  const [badgeDetail, setBadgeDetail] = useState<UserBadge | null>(null);

  // ── Identity resolution ────────────────────────────────────────────────────
  const handleParam = forceYou ? "@you" : (params.handle ?? "@you");
  const handle = handleParam.startsWith("@") ? handleParam : `@${handleParam}`;
  const isYou = handle === "@you" || forceYou === true;

  const heroUserId = isYou ? user?.id ?? null : otherProfile?.id ?? null;
  const profileId = !isYou ? (otherProfile?.id ?? null) : null;

  const { isFollowing, toggle } = useFollow();
  const following = !isYou && !!profileId && isFollowing(profileId);

  const { featured: featuredBadges, badges: allBadges } = useUserBadges(heroUserId);
  const { tier: collectorTier } = useCollectorTier(heroUserId);
  const earnedKindsCount = new Set(allBadges.map((b) => b.kind)).size;

  // ── Effects: fetch visitor data ────────────────────────────────────────────
  useEffect(() => {
    if (isYou) { setOtherProfile(null); setOtherScans([]); return; }
    let cancelled = false;
    const cleanHandle = handle.replace(/^@/, "");
    setOtherProfile(null);
    setOtherScans([]);
    (async () => {
      const { data: prof } = await supabase
        .from("profiles")
        .select("id, handle, display_name, avatar, bio, location, tags, looking_for, created_at")
        .eq("handle", cleanHandle)
        .maybeSingle();
      if (cancelled || !prof) return;
      setOtherProfile(prof as never);
      const { data: theirScans } = await supabase
        .from("scans")
        .select("id, title, img, era, category, price_suggested, verdict, posted, status, created_at, photos, posted_to, sold_at, sold_price, marketplace_links")
        .eq("user_id", prof.id)
        .eq("posted", true)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      setOtherScans((theirScans ?? []) as never);
    })();
    return () => { cancelled = true; };
  }, [isYou, handle]);

  useEffect(() => {
    if (isYou) return;
    let cancelled = false;
    const cleanHandle = handle.replace(/^@/, "");
    supabase
      .from("profiles")
      .select("marketplace_profiles")
      .eq("handle", cleanHandle)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const raw = (data as unknown as { marketplace_profiles?: unknown } | null)?.marketplace_profiles;
        setOtherMarketplaceProfiles(parseMarketplaceLinks(raw));
      });
    return () => { cancelled = true; };
  }, [isYou, handle]);

  useEffect(() => {
    if (isYou || !user || !profileId) return;
    let cancelled = false;
    supabase
      .from("post_notifications")
      .select("enabled")
      .eq("subscriber_id", user.id)
      .eq("target_id", profileId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setPostNotifyOn(!!(data as { enabled?: boolean } | null)?.enabled);
      });
    return () => { cancelled = true; };
  }, [isYou, user, profileId]);

  // Own marketplace profiles (for SHOP AT row + Settings echo)
  useEffect(() => {
    if (!isYou || !profile) { setMyMarketplaceProfiles([]); return; }
    const raw = (profile as unknown as { marketplace_profiles?: unknown }).marketplace_profiles;
    setMyMarketplaceProfiles(parseMarketplaceLinks(raw));
  }, [isYou, profile]);

  // Follower / following counts
  useEffect(() => {
    let cancelled = false;
    const targetId = isYou ? (profile?.id ?? null) : (otherProfile?.id ?? null);
    if (!targetId) return;
    (async () => {
      const [{ count: followers }, { count: followingN }] = await Promise.all([
        supabase.from("follows").select("*", { count: "exact", head: true }).eq("followed_id", targetId),
        supabase.from("follows").select("*", { count: "exact", head: true }).eq("follower_id", targetId),
      ]);
      if (cancelled) return;
      setFollowerCount(followers ?? 0);
      setFollowingCount(followingN ?? 0);
    })();
    return () => { cancelled = true; };
  }, [isYou, profile?.id, otherProfile?.id, following]);

  // ── Identity for render ────────────────────────────────────────────────────
  const d = isYou && profile
    ? {
        handle: profile.handle,
        display_name: profile.display_name,
        avatar: profile.avatar as SwatchKind | string,
        location: profile.location || "",
        tags: profile.tags || [],
        looking_for: profile.looking_for || [],
        created_at: profile.created_at,
      }
    : otherProfile
      ? {
          handle: otherProfile.handle,
          display_name: otherProfile.display_name,
          avatar: otherProfile.avatar as SwatchKind | string,
          location: otherProfile.location || "",
          tags: otherProfile.tags || [],
          looking_for: otherProfile.looking_for || [],
          created_at: otherProfile.created_at,
        }
      : {
          handle: handle.replace(/^@/, ""),
          display_name: null as string | null,
          avatar: "cream" as SwatchKind | string,
          location: "",
          tags: [] as string[],
          looking_for: [] as string[],
          created_at: "",
        };

  const memberNum = memberNumberFor(heroUserId);
  const sinceDate = formatSince(d.created_at);
  const mastheadName = (d.display_name?.trim() || d.handle || "you").trim();
  const handleDisplay = d.handle;
  const verifiedCount = isYou
    ? scans.filter((s) => s.verdict === "PASS").length
    : otherScans.filter((s) => s.verdict === "PASS").length;
  const bulkReview = bulkActive ? bulkPending : 0;
  const showBulkStat = isYou && bulkFeatures.canBulkUpload && !bulkLoading;
  const statsCount = showBulkStat ? 4 : 3;

  // ── Closet items ───────────────────────────────────────────────────────────
  const closetItems = useMemo(() => {
    if (isYou) {
      return scans.map((s) => {
        const photos = (s as { photos?: string[] }).photos ?? [];
        return {
          id: s.id,
          title: s.title,
          img: (s.img as SwatchKind) || "olive",
          brand: s.brand ?? "",
          era: s.era ?? "",
          verdict: s.verdict,
          posted: s.posted,
          soldAt: (s as { sold_at?: string | null }).sold_at ?? null,
          created_at: s.created_at,
          photo: photos[0] ?? null,
          marketplaceLinks: parseMarketplaceLinks((s as { marketplace_links?: unknown }).marketplace_links),
          postedTo: (s as { posted_to?: string | null }).posted_to ?? null,
        };
      });
    }
    return otherScans.map((s) => ({
      id: s.id,
      title: s.title,
      img: (s.img as SwatchKind) || "olive",
      brand: "",
      era: s.era ?? "",
      verdict: s.verdict,
      posted: s.posted,
      soldAt: s.sold_at ?? null,
      created_at: s.created_at,
      photo: (s.photos ?? [])[0] ?? null,
      marketplaceLinks: parseMarketplaceLinks(s.marketplace_links),
      postedTo: s.posted_to ?? null,
    }));
  }, [isYou, scans, otherScans]);

  const filteredItems = useMemo(() => {
    const filtered = closetItems.filter((it) => {
      if (statusTab === "all") return true;
      if (statusTab === "sold") return !!it.soldAt;
      if (statusTab === "listed") return it.posted && !it.soldAt;
      if (statusTab === "kept") return !it.posted && !it.soldAt;
      if (statusTab === "loves") return false; // not yet implemented
      return true;
    });
    return filtered.sort((a, b) => {
      if (sortBy === "oldest") return a.created_at.localeCompare(b.created_at);
      return b.created_at.localeCompare(a.created_at);
    });
  }, [closetItems, statusTab, sortBy]);

  const tabCounts = useMemo(() => {
    let kept = 0, listed = 0, sold = 0;
    for (const s of closetItems) {
      if (s.soldAt) sold++;
      else if (s.posted) listed++;
      else kept++;
    }
    return { all: closetItems.length, kept, listed, sold, loves: 0 };
  }, [closetItems]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const openItem = async (id: string) => {
    if (opening) return;
    if (!isYou) {
      nav(`/post/${id}`, { state: { from: window.location.pathname } });
      return;
    }
    setOpening(id);
    try {
      const { data, error } = await supabase
        .from("scans")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error("Item not found");

      const isManualPost = (data as { post_kind?: string }).post_kind === "manual";
      const hasNoResult = !data.result;
      const isPublic =
        !!data.posted ||
        !!(data as { sold_at?: string | null }).sold_at;
      // Route to /post (summary view) when:
      //   - it's a manual / unverified post (nothing to show on /result)
      //   - it's listed or sold — i.e. already in the public domain,
      //     and even the owner should see the public-facing view by
      //     default. They can drill into /result via /post's
      //     "View full report" button when they need the forensic view.
      // /result stays the default only for KEPT items (private, full
      // forensic for the owner only).
      if (isManualPost || hasNoResult || isPublic) {
        nav(`/post/${data.id}`, { state: { from: "/you" } });
        return;
      }

      const ai: AIScan = {
        title: data.title,
        brand: data.brand ?? "",
        era: data.era ?? "",
        origin: "",
        category: data.category ?? "",
        material: data.material ?? "",
        size: data.size ?? "",
        color: data.color ?? "",
        condition: data.condition ?? "",
        condition_notes: data.condition_notes ?? "",
        verdict: (data.verdict as "PASS" | "FAIL") ?? "PASS",
        verdict_label: data.verdict_label ?? "",
        confidence: data.confidence ?? 0,
        rarity: data.rarity ?? "",
        tags: data.tags ?? [],
        notes: data.notes ?? [],
        forensic: [],
        price_low: data.price_low != null ? Number(data.price_low) : 0,
        price_suggested: data.price_suggested != null ? Number(data.price_suggested) : 0,
        price_high: data.price_high != null ? Number(data.price_high) : 0,
      };
      scanStore.setResult(ai);
      scanStore.setPhotos((data.photos as string[]) ?? []);
      sessionStorage.setItem("fig_current_scan_id", data.id);
      sessionStorage.setItem("fig_scan_saved_id", data.id);
      nav("/result", { state: { from: "/you" } });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't open item";
      toast.error(msg);
    } finally {
      setOpening(null);
    }
  };

  const handleBlock = async () => {
    if (!user || !profileId) return;
    const handleLabel = otherProfile?.handle ? `@${otherProfile.handle}` : "this collector";
    if (!window.confirm(`Block ${handleLabel}? You'll stop seeing each other's posts and messages.`)) return;
    try {
      await blockUser(profileId);
      toast.success(`Blocked ${handleLabel}.`);
      setMenuOpen(false);
      nav(-1);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't block";
      toast.error(msg);
    }
  };

  const handleNotifyToggle = async () => {
    if (!user || !profileId || savingPostNotify) return;
    const next = !postNotifyOn;
    setSavingPostNotify(true);
    setPostNotifyOn(next);
    setMenuOpen(false);
    const { error } = await supabase
      .from("post_notifications")
      .upsert(
        {
          subscriber_id: user.id,
          target_id: profileId,
          enabled: next,
          updated_at: new Date().toISOString(),
        } as never,
        { onConflict: "subscriber_id,target_id" },
      );
    setSavingPostNotify(false);
    if (error) {
      setPostNotifyOn(!next);
      toast.error(error.message ?? "Couldn't update notifications");
      return;
    }
    toast.success(
      next ? "You'll be notified when they post." : "Post notifications turned off.",
    );
  };

  const handleShareProfile = async () => {
    const targetHandle = otherProfile?.handle ?? handle.replace(/^@/, "");
    const url = `${window.location.origin}/profile/${targetHandle}`;
    const navAny = navigator as unknown as {
      share?: (data: { title?: string; url?: string }) => Promise<void>;
    };
    if (navAny.share) {
      try {
        await navAny.share({ title: `@${targetHandle} on FIG`, url });
        setMenuOpen(false);
        return;
      } catch { /* user cancelled — fall through to clipboard */ }
    }
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success("Profile link copied.");
    setMenuOpen(false);
  };

  const messageThisProfile = async () => {
    if (openingThread) return;
    if (!user) { toast.error("Sign in to send a note."); return; }
    if (!otherProfile) { toast.error("Profile not loaded yet."); return; }
    if (otherProfile.id === user.id) { toast.info("That's you — open your inbox instead."); return; }
    setOpeningThread(true);
    try {
      const convoId = await ensureConversation(user.id, otherProfile.id);
      if (!convoId) { toast.error("Couldn't open the thread"); return; }
      nav(`/messages/${convoId}`);
    } finally {
      setOpeningThread(false);
    }
  };

  const connectionsBase = isYou ? "/you" : `/profile/${encodeURIComponent("@" + d.handle)}`;

  // Visitor view also gets Sold + Listed but no "Loves" (that's the visitor's own data, not seller's).
  // Owner sees the full Loves tab — placeholder for now until loves data is wired.
  const statusTabs: Array<{ key: typeof statusTab; label: string; n: number }> = [
    { key: "all",    label: "All",    n: tabCounts.all },
    { key: "kept",   label: "Kept",   n: tabCounts.kept },
    { key: "listed", label: "Listed", n: tabCounts.listed },
    { key: "sold",   label: "Sold",   n: tabCounts.sold },
    ...(isYou ? [{ key: "loves" as const, label: "Loves", n: tabCounts.loves }] : []),
  ];

  return (
    <MobileFrame>
      {/* ─── TOP TOOLBAR ─────────────────────────────────────────────────── */}
      <div style={{
        padding: "10px 12px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        gap: 6,
        background: BODY_BG,
      }}>
        <button
          onClick={() => nav(-1)}
          aria-label="Back"
          style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}
        >
          {Icon.chev(18, "l")}
        </button>
        {isYou ? (
          <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
            {isAdmin === true && (
              <button
                onClick={() => nav("/admin")}
                aria-label="Admin"
                title="Admin"
                style={{
                  background: "none", border: "1px solid hsl(var(--ink))",
                  borderRadius: 999,
                  padding: 4, cursor: "pointer", color: "hsl(var(--ink))",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 28, height: 28,
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 14, fontWeight: 700, letterSpacing: 0,
                  lineHeight: 1,
                }}
              >
                A
              </button>
            )}
            <NotificationsBell size="sm" />
            <button
              onClick={() => nav("/messages")}
              aria-label="Inbox"
              title="Inbox"
              style={{
                background: "none", border: "1px solid hsl(var(--ink))",
                borderRadius: 999,
                padding: 4, cursor: "pointer", color: "hsl(var(--ink))",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 28, height: 28, position: "relative",
              }}
            >
              {Icon.mail(14)}
              {unreadInbox > 0 && (
                <span
                  aria-label={`${unreadInbox} unread`}
                  style={{
                    position: "absolute", top: -6, right: -6,
                    minWidth: 18, height: 18, padding: "0 4px",
                    borderRadius: 999,
                    background: "hsl(var(--rust))",
                    color: "hsl(var(--cream))",
                    border: "1.5px solid hsl(var(--ink))",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 9, fontWeight: 700,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    lineHeight: 1,
                  }}
                >
                  {unreadInbox > 9 ? "9+" : unreadInbox}
                </span>
              )}
            </button>
            <button
              onClick={() => nav("/settings")}
              aria-label="Settings"
              title="Settings"
              style={{
                background: "none", border: "1px solid hsl(var(--ink))",
                borderRadius: 999,
                padding: 4, cursor: "pointer", color: "hsl(var(--ink))",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 28, height: 28,
              }}
            >
              {Icon.gear(14)}
            </button>
            <ExportSalesButton size="sm" />
          </div>
        ) : (
          <button
            onClick={() => setMenuOpen(true)}
            aria-label="More actions"
            title="More"
            style={{
              background: "none", border: "1px solid hsl(var(--ink))",
              borderRadius: 999,
              padding: 4, cursor: "pointer", color: "hsl(var(--ink))",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 28,
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 14, lineHeight: 1,
            }}
          >
            ···
          </button>
        )}
      </div>

      {/* ─── HERO ──────────────────────────────────────────────────────── */}
      <div style={{
        position: "relative",
        background: HERO_BG,
        color: HERO_FG,
        padding: "14px 20px 18px",
        backgroundImage: `
          repeating-linear-gradient(
            115deg,
            transparent 0px,
            transparent 12px,
            hsl(var(--ink-fg) / 0.04) 12px,
            hsl(var(--ink-fg) / 0.04) 13px
          )
        `,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
            fontSize: 10,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            opacity: 0.85,
            lineHeight: 1.4,
            whiteSpace: "nowrap",
          }}>
            FIG № {memberNum} · SINCE {sinceDate}
          </div>

          {d.avatar ? (
            <Swatch
              kind={d.avatar as SwatchKind | string}
              style={{
                width: 46, height: 46, borderRadius: 999,
                border: `1.5px solid ${HERO_FG}`,
                overflow: "hidden",
                flexShrink: 0,
              }}
            />
          ) : (
            <div style={{
              width: 46, height: 46, borderRadius: 999,
              border: `1.5px solid ${HERO_FG}`,
              display: "grid", placeItems: "center",
              fontFamily: "'Fraunces', serif", fontStyle: "italic", fontWeight: 900,
              fontSize: 20, lineHeight: 1,
            }}>
              {(d.display_name || d.handle || "?")[0]?.toUpperCase() ?? "?"}
            </div>
          )}
        </div>

        <div style={{
          fontFamily: "'Fraunces', serif",
          fontStyle: "italic",
          fontWeight: 900,
          fontVariationSettings: "'SOFT' 100, 'WONK' 1",
          fontSize: mastheadFontSize(mastheadName.length),
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
          marginTop: 2,
          paddingBottom: 2,
          whiteSpace: "nowrap",
          overflow: "visible",
        }}>
          {mastheadName}
        </div>
      </div>

      {/* ─── VISITOR ACTION ROW — Follow + Message ─────────────────────── */}
      {!isYou && (
        <div style={{ padding: "10px 16px 0", background: BODY_BG, display: "flex", gap: 8 }}>
          <button
            onClick={() => profileId && toggle(profileId)}
            className={following ? "btn" : "btn btn-primary"}
            style={{ flex: 2, padding: "10px 12px" }}
          >
            {following ? "✓ Following" : "Follow"}
          </button>
          <button
            className="btn"
            style={{
              flex: 1,
              padding: "10px 12px",
              opacity: openingThread ? 0.6 : 1,
              cursor: openingThread ? "wait" : "pointer",
            }}
            onClick={messageThisProfile}
            disabled={openingThread}
          >
            {openingThread ? "…" : "Message"}
          </button>
        </div>
      )}

      {/* ─── POSTCARD BIO ──────────────────────────────────────────────── */}
      <div style={{ padding: "12px 16px 6px", background: BODY_BG }}>
        <div style={{
          background: POSTCARD_BG_C,
          border: `1px solid ${BORDER_C}`,
          borderRadius: 14,
          padding: "16px 18px",
          position: "relative",
          boxShadow: "0 1px 0 hsl(var(--sand-deep) / 0.25)",
        }}>
          <div style={{
            position: "absolute",
            inset: 6,
            border: "1px dashed hsl(var(--sand-deep) / 0.45)",
            borderRadius: 9,
            pointerEvents: "none",
          }} />
          {isYou && (
            <button
              onClick={() => nav("/settings?tab=profile")}
              aria-label="Edit profile"
              style={{
                position: "absolute", top: 10, right: 10,
                padding: "3px 8px",
                background: "transparent",
                color: "hsl(var(--ink-2))",
                border: "1px solid hsl(var(--sand-deep) / 0.55)",
                borderRadius: 999,
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase",
                cursor: "pointer",
                zIndex: 1,
              }}
            >
              Edit
            </button>
          )}

          {/* FROM */}
          <PostcardRow
            label="FROM"
            value={d.location || <span style={{ opacity: 0.5 }}>—</span>}
          />

          <RowDivider />

          {/* LOOKING FOR — free-text wishlist set via Settings/onboarding.
              Falls back to interests-derived tags for users who pre-date
              the looking_for column, so existing profiles aren't blank. */}
          <PostcardRow
            label="LOOKING FOR"
            value={(() => {
              const hasWishlist = d.looking_for.length > 0;
              const items = hasWishlist
                ? d.looking_for
                : d.tags.map((t) => {
                    const known = INTERESTS.find((i) => i.value === t);
                    return known?.label ?? t;
                  });
              if (items.length === 0) return <span style={{ opacity: 0.5 }}>—</span>;
              return (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 2 }}>
                  {items.slice(0, 10).map((label, i) => (
                    <span key={`${label}-${i}`} style={{
                      display: "inline-block",
                      padding: "3px 10px",
                      borderRadius: 999,
                      border: "1px solid hsl(var(--sand-deep) / 0.45)",
                      background: BODY_BG,
                      fontFamily: "'Fraunces', serif",
                      fontSize: 13,
                      color: "hsl(var(--ink))",
                    }}>{label}</span>
                  ))}
                </div>
              );
            })()}
          />

          {/* SHOP AT — unified for both views. Uses the same PostcardRow
              chassis as FROM / LOOKING FOR for consistent rhythm. Edit
              affordance removed — owners manage shops via /settings. */}
          {(() => {
            const shops = isYou ? myMarketplaceProfiles : otherMarketplaceProfiles;
            if (!isYou && shops.length === 0) return null;
            return (
              <>
                <RowDivider />
                <PostcardRow
                  label="SHOP AT"
                  value={
                    shops.length > 0 ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 2 }}>
                        {shops.map((mp) =>
                          mp.url ? (
                            <a
                              key={mp.name + mp.url}
                              href={mp.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                padding: "3px 10px",
                                borderRadius: 999,
                                border: "1px solid hsl(var(--ink))",
                                background: BODY_BG,
                                fontFamily: "'Fraunces', serif",
                                fontSize: 13,
                                color: "hsl(var(--ink))",
                                textDecoration: "none",
                              }}
                            >
                              {mp.name} {Icon.share(11)}
                            </a>
                          ) : (
                            // Owner-only: a shop named but no URL yet.
                            <span
                              key={mp.name}
                              style={{
                                display: "inline-flex", alignItems: "center",
                                padding: "3px 10px",
                                borderRadius: 999,
                                border: "1px dashed hsl(var(--ink-2))",
                                background: BODY_BG,
                                fontFamily: "'Fraunces', serif",
                                fontSize: 13,
                                color: "hsl(var(--ink-2))",
                              }}
                            >
                              {mp.name}
                            </span>
                          ),
                        )}
                      </div>
                    ) : (
                      // Owner-only empty CTA — visitor row is hidden when
                      // shops is empty (handled by the early return above).
                      <span style={{
                        fontFamily: "'Fraunces', serif",
                        fontStyle: "italic",
                        fontSize: 14,
                        color: "hsl(var(--ink-2))",
                        opacity: 0.7,
                      }}>
                        Link your Depop, Etsy, eBay… in Settings →
                      </span>
                    )
                  }
                />
              </>
            );
          })()}
        </div>

        <div style={{
          fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
          fontSize: 10,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "hsl(var(--ink-2))",
          marginTop: 10,
          textAlign: "center",
        }}>
          @{handleDisplay}{d.location ? ` · ${d.location.toUpperCase()}` : ""} · COLLECTOR
        </div>
      </div>

      {/* ─── BADGE STRIP — slim row, sits between postcard and stats.
          Surfaces collectibles without competing with SHOP AT for the
          primary CTA real estate. Hidden when there's nothing to show
          (no tier + no badges + visitor view). */}
      {(collectorTier || featuredBadges.length > 0 || isYou) && (
        <div style={{ padding: "8px 16px 0", background: BODY_BG }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 0",
            borderTop: `1px solid ${BORDER_C}`,
            borderBottom: `1px solid ${BORDER_C}`,
          }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 10,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: LABEL_INK,
              flexShrink: 0,
            }}>
              {earnedKindsCount} OF {TOTAL_BADGE_KINDS}
            </span>
            <div style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              flex: 1,
              overflowX: "auto",
              minWidth: 0,
              scrollbarWidth: "none",
            }}>
              {collectorTier && (
                <button
                  type="button"
                  onClick={() => isYou ? nav("/you/badges") : undefined}
                  aria-label={`Collector tier: ${collectorTier}`}
                  style={{
                    background: "transparent", padding: 0, border: "none",
                    cursor: isYou ? "pointer" : "default",
                    flexShrink: 0,
                  }}
                >
                  <Chip filled>{collectorTier}</Chip>
                </button>
              )}
              {featuredBadges.slice(0, 3).map((b) => (
                <BadgeChip
                  key={b.id}
                  badge={b}
                  size="sm"
                  onClick={() => isYou ? nav("/you/badges") : setBadgeDetail(b)}
                />
              ))}
              {featuredBadges.length === 0 && !collectorTier && isYou && (
                <span style={{
                  fontFamily: "'Fraunces', serif",
                  fontStyle: "italic",
                  fontSize: 13,
                  color: "hsl(var(--ink-2))",
                  opacity: 0.7,
                }}>
                  No badges yet — earn one by getting verified.
                </span>
              )}
            </div>
            {isYou && (
              <button
                onClick={() => nav("/you/badges")}
                style={{
                  fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                  fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
                  color: LABEL_INK, background: "none", border: "none",
                  cursor: "pointer", padding: 0,
                  flexShrink: 0,
                }}
              >
                SEE ALL →
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── STATS STRIP ──────────────────────────────────────────────── */}
      <div style={{ padding: "10px 16px 6px", background: BODY_BG }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: `repeat(${statsCount}, 1fr)`,
          alignItems: "center",
          padding: "8px 0",
          borderTop: `1px solid ${BORDER_C}`,
          borderBottom: `1px solid ${BORDER_C}`,
        }}>
          <Stat
            n={followerCount ?? 0}
            label="Followers"
            onClick={() => nav(`${connectionsBase}/followers`)}
          />
          <Stat
            n={followingCount ?? 0}
            label="Following"
            divider
            onClick={() => nav(`${connectionsBase}/following`)}
          />
          <Stat n={verifiedCount} label="Verified" divider />
          {showBulkStat && (
            <Stat n={bulkReview} label="Bulk review" divider onClick={() => nav("/bulk/review")} />
          )}
        </div>
      </div>

      {/* ─── CLOSET ──────────────────────────────────────────────────── */}
      <div style={{ padding: "12px 12px 100px", background: BODY_BG }}>
        <div style={{ marginBottom: 12 }}>
          <h2 style={{
            fontFamily: "'Fraunces', serif",
            fontStyle: "italic",
            fontWeight: 800,
            fontSize: 26,
            margin: 0,
            color: "hsl(var(--ink))",
            letterSpacing: "-0.01em",
          }}>
            {isYou ? "Your closet" : `${mastheadName}'s closet`}
          </h2>
          <div style={{
            fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
            fontSize: 10,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "hsl(var(--ink-2))",
            textAlign: "right",
            marginTop: -22,
          }}>
            {tabCounts.all} ITEMS
          </div>
        </div>

        <div style={{ display: "flex", gap: 14, borderBottom: `1px solid ${BORDER_C}`, marginBottom: 14 }}>
          {statusTabs.map(({ key: k, label: lbl, n }) => {
            const active = statusTab === k;
            return (
              <button
                key={k}
                onClick={() => setStatusTab(k)}
                style={{
                  background: "none",
                  border: "none",
                  borderBottom: active ? `2px solid ${LABEL_INK}` : "2px solid transparent",
                  padding: "6px 0 8px",
                  cursor: "pointer",
                  color: active ? LABEL_INK : "hsl(var(--ink-2))",
                  fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  minWidth: 38,
                }}
              >
                <span>{lbl}</span>
                <span style={{ opacity: 0.7, fontSize: 10 }}>{n}</span>
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 14, overflowX: "auto" }}>
          {([
            ["newest", "Newest"],
            ["oldest", "Oldest"],
          ] as const).map(([k, lbl]) => {
            const active = sortBy === k;
            return (
              <button
                key={k}
                onClick={() => setSortBy(k)}
                style={{
                  padding: "6px 14px",
                  borderRadius: 999,
                  border: active ? "none" : `1px solid ${BORDER_C}`,
                  background: active ? "hsl(var(--ink))" : "transparent",
                  color: active ? "hsl(43 56% 95%)" : "hsl(var(--ink))",
                  fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                  fontSize: 11,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {lbl}
              </button>
            );
          })}
        </div>

        {filteredItems.length === 0 ? (
          <div style={{
            padding: "32px 16px",
            textAlign: "center",
            color: "hsl(var(--ink-2))",
            fontFamily: "'Fraunces', serif",
            fontStyle: "italic",
          }}>
            Nothing here yet.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, alignItems: "start" }}>
            {filteredItems.map((it) => {
              const isSold = !!it.soldAt;
              const isListed = it.posted && !isSold;
              const isKept = !it.posted && !isSold;
              // When listed, prefer showing the marketplace name (EBAY, DEPOP,
              // etc.) over the generic 'LISTED'. The badge becomes a direct
              // link to the seller's listing if a URL is on file. Falls back
              // to plain 'LISTED' when no marketplace_links exist and
              // posted_to is null (legacy rows or quick-posts).
              const primaryListing =
                isListed
                  ? (it.marketplaceLinks?.find((m) => m.name && m.url) ??
                     (it.postedTo ? { name: it.postedTo, url: "" } : null))
                  : null;
              const statusBadge =
                isSold   ? "SOLD"   :
                isListed ? (primaryListing?.name?.toUpperCase() ?? "LISTED") :
                isKept   ? "KEPT"   : null;
              const statusBg =
                isSold   ? "hsl(var(--ink))"      :
                isListed ? LABEL_INK              :
                isKept   ? "hsl(var(--sand-deep))": null;
              const statusFg = HERO_FG;
              const isOpening = opening === it.id;
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => openItem(it.id)}
                  disabled={isOpening}
                  style={{
                    borderRadius: 12,
                    overflow: "hidden",
                    background: POSTCARD_BG_C,
                    border: `1px solid ${BORDER_C}`,
                    padding: 0,
                    textAlign: "left",
                    cursor: isOpening ? "default" : "pointer",
                    opacity: isOpening ? 0.55 : 1,
                    transition: "opacity 120ms ease",
                  }}
                >
                  <div style={{ position: "relative", aspectRatio: "1/1", overflow: "hidden" }}>
                    {it.photo ? (
                      <img src={it.photo} alt={it.title} style={{
                        width: "100%", height: "100%", objectFit: "cover", display: "block",
                      }} />
                    ) : (
                      <Swatch kind={it.img} style={{ width: "100%", height: "100%" }} />
                    )}
                  </div>
                  {it.verdict === "PASS" && (
                    <div style={{
                      padding: "5px 9px 0",
                      fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                      fontSize: 9, letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: LABEL_INK,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      width: "100%",
                      boxSizing: "border-box",
                      whiteSpace: "nowrap",
                    }}>
                      ✓ FIG Verified
                    </div>
                  )}
                  <div style={{ padding: "6px 9px 8px" }}>
                    <div style={{
                      fontFamily: "'Fraunces', serif",
                      fontSize: 13, lineHeight: 1.2, color: "hsl(var(--ink))",
                      overflow: "hidden", textOverflow: "ellipsis",
                      display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                    }}>{it.title}</div>
                    {it.brand && (
                      <div style={{
                        fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                        fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase",
                        color: "hsl(var(--ink-2))", marginTop: 4,
                      }}>{it.brand}</div>
                    )}
                    <div style={{
                      fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                      fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase",
                      color: "hsl(var(--ink-2))", marginTop: 2, opacity: 0.7,
                    }}>{new Date(it.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                  </div>
                  <div style={{
                    padding: "5px 8px",
                    borderTop: `1px solid ${BORDER_C}`,
                    background: BODY_BG,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 6,
                  }}>
                    {statusBadge ? (
                      primaryListing?.url ? (
                        // Listed with a real URL — render as an anchor.
                        // stopPropagation so a tap opens the shop link
                        // instead of also firing the tile's openItem.
                        <a
                          href={primaryListing.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            background: statusBg!,
                            color: statusFg,
                            fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                            fontSize: 9, letterSpacing: "0.14em",
                            padding: "3px 8px",
                            borderRadius: 3,
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          {statusBadge} {Icon.share(9)}
                        </a>
                      ) : (
                        <span style={{
                          background: statusBg!,
                          color: statusFg,
                          fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                          fontSize: 9, letterSpacing: "0.14em",
                          padding: "3px 8px",
                          borderRadius: 3,
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                        }}>{statusBadge}</span>
                      )
                    ) : <span />}
                    {/* Edit pencil — only on own profile */}
                    {isYou && (
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label="Edit item"
                        title="Edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          nav(`/listing/${it.id}/edit`);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                            e.preventDefault();
                            nav(`/listing/${it.id}/edit`);
                          }
                        }}
                        style={{
                          color: "hsl(var(--ink-2))",
                          width: 20, height: 20, padding: 0, cursor: "pointer",
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          lineHeight: 1, flexShrink: 0, opacity: 0.75,
                        }}
                      >{Icon.pencil(12)}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* ─── Feedback CTA — own profile only ─────────────────────────
            Surfacing the 'got an idea?' prompt on a high-frequency
            screen (vs. burying it in settings) so users actually
            remember to send feedback when something inspires them. */}
        {isYou && (
          <button
            type="button"
            onClick={() => nav("/support")}
            style={{
              display: "block",
              width: "100%",
              marginTop: 24,
              padding: "16px 18px",
              background: "transparent",
              border: `1px dashed ${BORDER_C}`,
              borderRadius: 12,
              textAlign: "left",
              cursor: "pointer",
              color: "hsl(var(--ink))",
            }}
          >
            <div className="caps" style={{ color: LABEL_INK, marginBottom: 4 }}>
              Got an idea?
            </div>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontStyle: "italic",
              fontSize: 15,
              lineHeight: 1.4,
              color: "hsl(var(--ink))",
            }}>
              Tell us what would make FIG better — bugs, features, brands
              we missed. We read every one. <span style={{ color: LABEL_INK, fontStyle: "normal", letterSpacing: "0.06em" }}>→</span>
            </div>
          </button>
        )}
      </div>

      <BottomDock />

      {/* ─── 3-dot actions menu (visitor only) ───────────────────────── */}
      {!isYou && menuOpen && profileId && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Profile actions"
          onClick={() => setMenuOpen(false)}
          style={{
            position: "fixed", inset: 0,
            background: "hsla(0,0%,0%,0.55)",
            zIndex: 100,
            display: "flex", alignItems: "flex-end", justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 430,
              background: "hsl(var(--cream))",
              borderTop: "1.5px solid hsl(var(--ink))",
              padding: "14px 16px calc(28px + env(safe-area-inset-bottom, 0px))",
              display: "flex", flexDirection: "column", gap: 6,
            }}
          >
            <div className="caps" style={{
              textAlign: "center", color: "hsl(var(--ink-2))", marginBottom: 6,
            }}>
              {otherProfile?.handle ? `@${otherProfile.handle}` : "Profile"}
            </div>

            <button
              type="button"
              className="btn"
              onClick={handleBlock}
              style={{ width: "100%", padding: "12px", textAlign: "left", color: "hsl(var(--rust))" }}
            >
              Block {otherProfile?.handle ? `@${otherProfile.handle}` : "this user"}
            </button>

            <button
              type="button"
              className="btn"
              onClick={() => { setMenuOpen(false); setReportOpen(true); }}
              style={{ width: "100%", padding: "12px", textAlign: "left" }}
            >
              Report profile
            </button>

            <button
              type="button"
              className="btn"
              onClick={handleNotifyToggle}
              disabled={savingPostNotify}
              style={{ width: "100%", padding: "12px", textAlign: "left", opacity: savingPostNotify ? 0.6 : 1 }}
            >
              {postNotifyOn ? "Turn off post notifications" : "Notify me when they post"}
            </button>

            <button
              type="button"
              className="btn"
              onClick={handleShareProfile}
              style={{ width: "100%", padding: "12px", textAlign: "left" }}
            >
              Share profile
            </button>

            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              style={{
                marginTop: 8,
                background: "transparent",
                border: "1px dashed hsl(var(--ink-2))",
                padding: "10px",
                width: "100%",
                cursor: "pointer",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
                color: "hsl(var(--ink-2))",
              }}
            >
              Cancel
            </button>
          </div>
        </div>,
        document.body,
      )}

      {/* Profile report dialog */}
      {!isYou && profileId && (
        <ReportDialog
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          targetType="profile"
          targetId={profileId}
        />
      )}

      {/* Badge detail modal (visitor view tap on a chip) */}
      {badgeDetail && (
        <BadgeDetailModal badge={badgeDetail} onClose={() => setBadgeDetail(null)} />
      )}
    </MobileFrame>
  );
}

function PostcardRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ padding: "8px 4px" }}>
      <div style={{
        fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
        fontSize: 10,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: LABEL_INK,
        marginBottom: 4,
      }}>{label}</div>
      <div style={{
        fontFamily: "'Fraunces', serif",
        fontSize: 16,
        lineHeight: 1.3,
        color: "hsl(var(--ink))",
      }}>{value}</div>
    </div>
  );
}

function RowDivider() {
  return <div style={{ borderTop: "1px dashed hsl(var(--sand-deep) / 0.45)", margin: "0 -4px" }} />;
}

function Stat({
  n, label, divider, onClick,
}: { n: number; label: string; divider?: boolean; onClick?: () => void }) {
  const inner = (
    <>
      <div style={{
        fontFamily: "'Fraunces', serif",
        fontStyle: "italic",
        fontWeight: 800,
        fontSize: 22,
        lineHeight: 1,
        color: "hsl(var(--ink))",
      }}>{n}</div>
      <div style={{
        fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
        fontSize: 9,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: "hsl(var(--ink-2))",
        marginTop: 3,
      }}>{label}</div>
    </>
  );
  const dividerStyle: React.CSSProperties = divider
    ? { borderLeftWidth: 1, borderLeftStyle: "solid", borderLeftColor: BORDER_C }
    : { borderLeftWidth: 0, borderLeftStyle: "solid", borderLeftColor: "transparent" };
  const baseStyle: React.CSSProperties = {
    textAlign: "center",
    padding: "2px 0",
    ...dividerStyle,
  };
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          ...baseStyle,
          borderTopWidth: 0, borderRightWidth: 0, borderBottomWidth: 0,
          borderRadius: 0,
          background: "none",
          appearance: "none",
          WebkitAppearance: "none",
          cursor: "pointer",
          color: "inherit",
          font: "inherit",
        }}
      >
        {inner}
      </button>
    );
  }
  return <div style={baseStyle}>{inner}</div>;
}
